import React, { useState } from 'react';
import { ShoppingCart, Search, Star, Upload, CreditCard, Smartphone, Plus, Minus, X, Package, Clock, CheckCircle, Mail } from 'lucide-react';
import { Product, CartItem, Order } from '../types';
import { showToast } from '../utils/toast';
import CheckoutModal from './Shop/CheckoutModal';

interface ShopProps {
  products: Product[];
  cart: CartItem[];
  onAddToCart: (productId: string, quantity: number) => void;
  onUpdateCart: (productId: string, quantity: number) => void;
  onRemoveFromCart: (productId: string) => void;
  onCheckout: (order: Omit<Order, 'id'>) => void;
  currentUser?: any | null;
  handleAuthRequired?: (message: string, mode?: 'signin' | 'signup') => void;
  onNavigate: (section: string, data?: any) => void;
}

const Shop: React.FC<ShopProps> = ({
  products,
  cart,
  onAddToCart,
  onUpdateCart,
  onRemoveFromCart,
  onCheckout,
  currentUser,
  handleAuthRequired,
  onNavigate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [prescriptionFiles, setPrescriptionFiles] = useState<{[key: string]: File}>({});
  const [showPastOrders, setShowPastOrders] = useState(false);
  const [orderConfirmation, setOrderConfirmation] = useState<string | null>(null);

  const categories = Array.from(new Set(products.map(p => p.category)));

  const filteredProducts = products
    .filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(product => !selectedCategory || product.category === selectedCategory);

  const cartTotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const pastOrders = [
    {
      id: 'ORD-001',
      date: '2024-01-15',
      total: 45.50,
      status: 'delivered',
      items: [
        { name: 'Paracetamol 500mg', quantity: 2, price: 2.50 },
        { name: 'Vitamin D3 1000IU', quantity: 1, price: 12.99 }
      ]
    },
    {
      id: 'ORD-002',
      date: '2024-01-10',
      total: 28.75,
      status: 'shipped',
      items: [
        { name: 'Blood Pressure Monitor', quantity: 1, price: 45.00 }
      ]
    }
  ];

  // Email notification functions
  const sendOrderConfirmationEmail = async (orderId: string, userEmail: string, orderDetails: any) => {
    // In a real app, this would call your email service
    console.log('Sending order confirmation email:', {
      to: userEmail,
      orderId,
      orderDetails
    });
    
    // Simulate email sending
    setTimeout(() => {
      showToast.system.success(`Order confirmation email sent to ${userEmail}`);
    }, 1000);
  };

  const sendOrderCancellationEmail = async (orderId: string, userEmail: string) => {
    // In a real app, this would call your email service
    console.log('Sending order cancellation email:', {
      to: userEmail,
      orderId
    });
    
    // Simulate email sending
    setTimeout(() => {
      showToast.system.success(`Order cancellation email sent to ${userEmail}`);
    }, 1000);
  };

  const handlePrescriptionUpload = (productId: string, file: File) => {
    setPrescriptionFiles(prev => ({ ...prev, [productId]: file }));
    showToast.upload.success(file.name);
  };

  const handleAddToCartWithToast = (productId: string, quantity: number) => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to add items to your cart', 'signup');
      return;
    }

    const product = products.find(p => p.id === productId);
    if (product) {
      onAddToCart(productId, quantity);
      showToast.shop.addedToCart(product.name);
    }
  };

  const handleUpdateCartWithToast = (productId: string, quantity: number) => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to update your cart', 'signin');
      return;
    }

    onUpdateCart(productId, quantity);
    showToast.shop.cartUpdated();
  };

  const handleRemoveFromCartWithToast = (productId: string) => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to manage your cart', 'signin');
      return;
    }

    const product = cart.find(item => item.productId === productId)?.product;
    if (product) {
      onRemoveFromCart(productId);
      showToast.shop.removedFromCart(product.name);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!currentUser) return;
    
    // Send cancellation email
    await sendOrderCancellationEmail(orderId, currentUser.email);
    showToast.system.success(`Order ${orderId} has been cancelled`);
  };

  // Handle backdrop clicks for modal dismissal
  const handleModalBackdropClick = (e: React.MouseEvent, closeModal: () => void) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Product Detail View Modal
  const ProductDetailModal = ({ product }: { product: Product }) => {
    const cartItem = cart.find(item => item.productId === product.id);
    const quantity = cartItem?.quantity || 0;

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => handleModalBackdropClick(e, () => setSelectedProduct(null))}
      >
        <div className="card-base w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-custom dark:bg-gray-900 dark:shadow-xl dark:border dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Product Details</h3>
            <button
              onClick={() => setSelectedProduct(null)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors text-2xl"
            >
              ×
            </button>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <img 
                  src={product.images[0] || 'https://images.pexels.com/photos/3683107/pexels-photo-3683107.jpeg'} 
                  alt={product.name}
                  className="w-full h-64 object-cover rounded-xl"
                />
                
                <div className="flex flex-wrap gap-2 mt-4">
                  {!product.inStock && (
                    <span className="badge-error">
                      Out of Stock
                    </span>
                  )}
                  {product.prescriptionRequired && (
                    <span className="badge-warning">
                      Prescription Required
                    </span>
                  )}
                  <span className="badge-success">
                    {product.stockCount} in stock
                  </span>
                </div>
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{product.name}</h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{product.description}</p>
                
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={16} 
                        className={i < Math.floor(product.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'} 
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">({product.reviews} reviews)</span>
                </div>
                
                <div className="mb-6">
                  <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">${product.price.toFixed(2)}</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">per unit</span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Manufacturer</h4>
                    <p className="text-gray-600 dark:text-gray-300">{product.manufacturer}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Category</h4>
                    <span className="badge-secondary">
                      {product.category}
                    </span>
                  </div>
                  
                  {product.tags.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {product.tags.map((tag, index) => (
                          <span key={index} className="badge-base bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {product.prescriptionRequired && (
              <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800 border border-yellow-200 rounded-xl">
                <h4 className="font-medium text-yellow-800 dark:text-yellow-100 mb-3">Prescription Required</h4>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm mb-3">
                  This medication requires a valid prescription. Please upload your prescription to proceed.
                </p>
                <label className="flex items-center space-x-3 cursor-pointer bg-yellow-100 dark:bg-yellow-900 dark:hover:bg-yellow-800 hover:bg-yellow-200 transition-colors p-3 rounded-lg">
                  <Upload size={20} className="text-yellow-600 dark:text-yellow-400" />
                  <span className="text-yellow-700 dark:text-yellow-300 font-medium">Upload Prescription</span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePrescriptionUpload(product.id, file);
                    }}
                    className="hidden"
                  />
                </label>
                {prescriptionFiles[product.id] && (
                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-950 dark:border-green-800 border border-green-200 rounded-lg">
                    <p className="text-green-700 dark:text-green-300 text-sm font-medium">
                      ✓ Prescription uploaded: {prescriptionFiles[product.id].name}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between">
                {quantity > 0 ? (
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Quantity:</span>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleUpdateCartWithToast(product.id, quantity - 1)}
                        className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg dark:text-gray-100">{quantity}</span>
                      <button
                        onClick={() => handleUpdateCartWithToast(product.id, quantity + 1)}
                        className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      handleAddToCartWithToast(product.id, 1);
                      setSelectedProduct(null);
                    }}
                    disabled={!product.inStock || (product.prescriptionRequired && !prescriptionFiles[product.id])}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add to Cart
                  </button>
                )}
                
                <div className="text-right">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    ${(product.price * Math.max(quantity, 1)).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Cart Modal
  const CartModal = () => (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => handleModalBackdropClick(e, () => setShowCart(false))}
    >
      <div className="card-base w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-custom dark:bg-gray-900 dark:shadow-xl dark:border dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Shopping Cart</h3>
          <button
            onClick={() => setShowCart(false)}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6">
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="mx-auto text-gray-400 dark:text-gray-500 mb-4" size={64} />
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Your cart is empty</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-6">Add some products to get started</p>
              <button
                onClick={() => setShowCart(false)}
                className="btn-primary"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={item.productId} className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <img 
                      src={item.product.images[0] || 'https://images.pexels.com/photos/3683107/pexels-photo-3683107.jpeg'} 
                      alt={item.product.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{item.product.name}</h4>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">${item.product.price.toFixed(2)} each</p>
                      {item.product.prescriptionRequired && item.prescriptionUploaded && (
                        <span className="text-green-600 dark:text-green-400 text-xs font-medium">✓ Prescription uploaded</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleUpdateCartWithToast(item.productId, item.quantity - 1)}
                        className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="font-medium dark:text-gray-100">{item.quantity}</span>
                      <button
                        onClick={() => handleUpdateCartWithToast(item.productId, item.quantity + 1)}
                        className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold dark:text-gray-100">${(item.product.price * item.quantity).toFixed(2)}</p>
                      <button
                        onClick={() => handleRemoveFromCartWithToast(item.productId)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-xl font-bold text-gray-900 dark:text-gray-100">Total: ${cartTotal.toFixed(2)}</span>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {cartItemCount} item{cartItemCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCart(false);
                    setShowCheckout(true);
                  }}
                  className="btn-primary w-full py-4 text-lg"
                >
                  Proceed to Checkout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Order Confirmation Modal
  const OrderConfirmationModal = () => (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => handleModalBackdropClick(e, () => setOrderConfirmation(null))}
    >
      <div className="card-base w-full max-w-lg dark:bg-gray-900 dark:shadow-xl dark:border dark:border-gray-700">
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600 dark:text-green-400" size={32} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Order Confirmed!</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Your order #{orderConfirmation} has been successfully placed.
          </p>
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Mail className="text-blue-600 dark:text-blue-400" size={16} />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Confirmation email sent to {currentUser?.email}
            </p>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            You'll receive SMS updates with tracking details.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setOrderConfirmation(null);
                onNavigate('profile', { tab: 'orders' });
              }}
              className="btn-primary w-full"
            >
              View Order Details
            </button>
            <button
              onClick={() => setOrderConfirmation(null)}
              className="btn-ghost w-full"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const ProductCard = ({ product }: { product: Product }) => {
    const cartItem = cart.find(item => item.productId === product.id);
    const quantity = cartItem?.quantity || 0;

    return (
      <div className="card-interactive p-4 dark:bg-gray-800 dark:border-gray-700 dark:hover:shadow-md dark:hover:border-gray-600">
        <div 
          onClick={() => setSelectedProduct(product)}
          className="cursor-pointer"
        >
          <div className="relative mb-3">
            <img 
              src={product.images[0] || 'https://images.pexels.com/photos/3683107/pexels-photo-3683107.jpeg'} 
              alt={product.name}
              className="w-full h-32 object-cover rounded-lg group-hover:scale-105 transition-transform"
            />
            {!product.inStock && (
              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                <span className="badge-error">
                  Out of Stock
                </span>
              </div>
            )}
            {product.prescriptionRequired && (
              <span className="absolute top-2 right-2 badge-error">
                Rx
              </span>
            )}
          </div>
          
          <div className="mb-3">
            <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1 line-clamp-2 group-hover:text-green-600 transition-colors">
              {product.name}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-2 line-clamp-2">{product.description}</p>
            <div className="flex items-center space-x-2 mb-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={12} 
                    className={i < Math.floor(product.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'} 
                  />
                ))}
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400">({product.reviews})</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-bold text-gray-900 dark:text-gray-100">${product.price.toFixed(2)}</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">{product.stockCount} left</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          {quantity > 0 ? (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleUpdateCartWithToast(product.id, quantity - 1)}
                className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                <Minus size={12} />
              </button>
              <span className="font-medium text-sm dark:text-gray-100">{quantity}</span>
              <button
                onClick={() => handleUpdateCartWithToast(product.id, quantity + 1)}
                className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-colors"
              >
                <Plus size={12} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => handleAddToCartWithToast(product.id, 1)}
              disabled={!product.inStock}
              className="btn-primary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add to Cart
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 pb-20 lg:pb-0">
      {/* Header */}
      <div className="glass border-b border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">PharmPlus Shop</h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Your trusted online pharmacy</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => onNavigate('profile', { tab: 'orders' })}
                className="btn-ghost flex items-center space-x-2"
              >
                <Package size={16} />
                <span className="hidden sm:inline">Orders</span>
              </button>
              
              <button
                onClick={() => setShowCart(true)}
                className="btn-primary relative flex items-center space-x-2"
              >
                <ShoppingCart size={16} />
                <span className="hidden sm:inline">Cart</span>
                {cartItemCount > 0 && (
                  <span className="notification-dot">
                    {cartItemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search medications and health products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-base pl-9 w-full"
              />
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="select-base w-full sm:w-auto"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <ShoppingCart className="mx-auto text-gray-400 dark:text-gray-500 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No products found</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">Try adjusting your search or browse all categories.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedProduct && <ProductDetailModal product={selectedProduct} />}
      {showCart && <CartModal />}
      {showCheckout && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          cart={cart}
          cartTotal={cartTotal}
          onCheckout={onCheckout}
          currentUser={currentUser}
          onOrderConfirmation={setOrderConfirmation}
        />
      )}
      {orderConfirmation && <OrderConfirmationModal />}
    </div>
  );
};

export default Shop;